import React, { useEffect, memo, useState, useCallback } from 'react'
import { observer } from 'mobx-react'
import { NavigateFunction } from 'react-router'
import { Typography, Button, Box } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import {
  RegisteredEventName,
  broadcastToParent
} from '@finanzcheck/catalyst-pollard'
import { getButtonSizes } from 'components/FormNavigation/helpers/getButtonSizes'
import { useSaveButton } from 'hooks/useSaveButton'
import useStore from 'stores/utils/hooks/useStore'
import useEmotionStyles from 'hooks/useEmotionStyles'
import LegalHintTexts from 'texts/common/copy/legal'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import {
  IBAN_PATH,
  usePayoutAccountValidation
} from 'hooks/usePayoutAccountValidation'
import {
  CONFIG_DDF_SMAVA,
  CONFIG_SECOND_DEBTOR_SMAVA,
  isRceEditConfig
} from 'config/utils/config'
import { useField } from 'react-final-form'
import NavigationWrapper from 'components/FormNavigation/components/NavigationWrapper'
import { AgeGroup } from 'constants/components'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useSmavaSkipDacButton } from 'components/FormNavigation/components/SmavaFormNavigation/hooks/useSmavaSkipDacButton'
import useUserState from 'hooks/useUserState'
import { trackWithOrigin } from 'components/FormNavigation/helpers/trackWithOrigin'
import { useDacAutoAdvance } from 'components/FormNavigation/hooks/useDacAutoAdvance'
import { getBackNavigationLabel } from 'components/FormNavigation/helpers/getBackNavigationLabel'
import { getShowDacLegalText } from 'components/FormNavigation/helpers/getShowDacLegalText'
import { shouldRenderCat4784SkipBelowBack } from 'components/FormNavigation/components/SmavaFormNavigation/helpers/shouldRenderCat4784SkipBelowBack'
import { smavaFormNavigationStyles } from './SmavaFormNavigation.styles'
import { useGate } from 'gates'
import DacProcessingIndicator from 'containers/Smava/DAC/components/DacProcessingIndicator'

type Props = {
  onBack?: () => void
  onNext: () => void
  navigate: NavigateFunction
}

const FormNavigation = observer(({ onBack, onNext, navigate }: Props) => {
  const {
    setIsSkipDacCTAVisible,
    isDacShowOnCtaClick,
    isDacProcessingIndicatorVisible,
    isInIframe,
    urlParams: { backUrl }
  } = useStore()

  const isPayoutAccountReady = usePayoutAccountValidation(true)
  const [
    isIbanPresentOnPayoutAccountPage,
    setIsIbanPresentOnPayoutAccountPage
  ] = useState(false)

  const {
    page: {
      isFirstDisplayedPage,
      getCurrentIndex,
      isLastDisplayedPage,
      config
    },
    navigation: { currentPageInSession, isDacPage },
    traversal: { data },
    traversalSession: { dacWidgetState }
  } = useStore()

  const isLastPage = isLastDisplayedPage(currentPageInSession)
  const { isPremium, isMiddleAge, isOldAge, ageGroup, isAnyOf } = useUserState()
  const isPremiumOrOldAge = isAnyOf('isPremium', 'isOldAge')
  const isCat4784Experiment = useGate('cat4784SmavaStandardDacExperiment')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const ibanField = useField(IBAN_PATH, {})

  const { isSaveButtonVisible } = useSaveButton({
    onSuccess: () => {},
    onError: () => {}
  })

  const isYoungOrMiddleAgeGroup = isAnyOf('isYoungAge', 'isMiddleAge')

  const isMiddleAgeGroup = isMiddleAge

  const isRceConfig = isRceEditConfig(config.name)

  const { showDacSkipButton, skipButtonText, skipButtonShownReason } =
    useSmavaSkipDacButton({
      isDacPage,
      isDacShowOnCtaClick,
      isPremium,
      isPremiumOrOldAge,
      isYoungOrMiddleAgeGroup,
      ageGroup,
      dacWidgetState,
      isDacSkipButtonTriggersIgnored: isMiddleAgeGroup && !isPremiumOrOldAge,
      fallbackButtonText: 'Ich habe keinen Online-Banking-Zugang'
    })

  const showMiddleAgeOrRceDacSkipButton =
    (isMiddleAgeGroup &&
      isDacPage &&
      !isPremiumOrOldAge &&
      !isRceConfig &&
      config.name !== CONFIG_SECOND_DEBTOR_SMAVA) ||
    (isRceConfig && isDacPage)

  const isDacSkipButtonVisible =
    isDacPage && (showMiddleAgeOrRceDacSkipButton || showDacSkipButton)
  const isDacError = skipButtonShownReason === 'error_in_dac'
  const shouldRenderSkipBelowBack = shouldRenderCat4784SkipBelowBack({
    isTreatment: isCat4784Experiment,
    isSkipButtonVisible: isDacSkipButtonVisible,
    isPremium,
    isOldAge,
    isMobile,
    isDacError
  })
  const skipDacButtonVariant =
    isCat4784Experiment && !isPremium && !isDacError && (!isOldAge || !isMobile)
      ? 'text'
      : 'outlined'

  useEffect(() => {
    setIsIbanPresentOnPayoutAccountPage(
      currentPageInSession === 'payoutAccount' &&
        ibanField.input.value &&
        dacWidgetState === 'initial'
    )
  }, [data, currentPageInSession, dacWidgetState])

  const currentIndex = getCurrentIndex()

  const onNextWithTracking = useCallback(() => {
    onNext()
  }, [onNext, data])

  useDacAutoAdvance({ dacWidgetState, onNext })

  useEffect(() => {
    if (showDacSkipButton) {
      setIsSkipDacCTAVisible(true)
    }
  }, [showDacSkipButton])

  const skipDacButtonOnClick = useCallback(() => {
    broadcastToParent({
      eventName: RegisteredEventName.tracking,
      data: {
        event: 'skip_button_clicked',
        context: {
          Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
          ...getOptimizelyTrackingAttribute(data)
        },
        payload: {
          text_on_button: skipButtonText
        }
      }
    })
    onNext()
  }, [onNext, skipButtonText])

  const { buttonTitle, buttonHint, buttonTextContainer } = useEmotionStyles(
    smavaFormNavigationStyles
  )

  const isFirstPage = isFirstDisplayedPage(currentPageInSession)

  const getSubmitLabel = () => {
    if (isFirstPage || isLastPage) {
      const title =
        isLastPage || ageGroup === AgeGroup.OLD_AGE_GROUP
          ? 'Weiter zu den Angeboten'
          : 'Kreditvergleich starten'

      const hint = 'kostenlos und unverbindlich'

      return (
        <div className={buttonTextContainer}>
          <Typography className={buttonTitle}>{title}</Typography>
          <Typography className={buttonHint}>{hint}</Typography>
        </div>
      )
    }

    return 'Weiter'
  }

  const { backLabel, isBackButtonHidden } = getBackNavigationLabel({
    isInIframe,
    currentIndex,
    backUrl,
    isFirstPage
  })

  const getIsContinueButtonHidden = () => {
    if (isRceConfig) {
      return isLastPage && dacWidgetState !== 'success'
    }

    if (isLastPage && isPremiumOrOldAge && isDacPage) {
      return true
    }

    if (config.name === CONFIG_SECOND_DEBTOR_SMAVA) {
      return false
    }

    return (
      isLastPage &&
      isPayoutAccountReady &&
      dacWidgetState !== 'success' &&
      !isIbanPresentOnPayoutAccountPage &&
      isDacPage &&
      ageGroup !== AgeGroup.OLD_AGE_GROUP
    )
  }

  const showDacLegalText = getShowDacLegalText({
    isLastPage,
    isPayoutAccountReady,
    isDacPage,
    configName: config.name,
    targetConfigName: CONFIG_DDF_SMAVA
  })

  const isContinueButtonHidden = getIsContinueButtonHidden()

  const { sizeOfContinueButton, sizeOfBackButton, sizeOfSaveButton } =
    getButtonSizes(
      isBackButtonHidden,
      isSaveButtonVisible,
      isContinueButtonHidden
    )

  const onBackHandler = () => {
    if (isLastPage) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'back_button_click_on_dac_page',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
    }
    onBack && onBack()
  }

  return (
    <NavigationWrapper
      onBack={onBackHandler}
      onNext={onNext}
      navigate={navigate}
      legalText={LegalHintTexts.smavaLegalHint}
      isContinueWithoutDacVisible={false}
      isBackButtonHidden={isBackButtonHidden}
      isContinueButtonHidden={isContinueButtonHidden}
      sizeOfContinueButton={sizeOfContinueButton}
      sizeOfBackButton={
        isRceConfig && isDacSkipButtonVisible ? 4 : sizeOfBackButton
      }
      sizeOfSaveButton={
        isDacSkipButtonVisible && !shouldRenderSkipBelowBack
          ? 4
          : sizeOfSaveButton
      }
      submitLabel={getSubmitLabel()}
      backLabel={backLabel}
      sizeOfSkipDacButton={8}
      onNextWithTracking={onNextWithTracking}
      trackWithOrigin={trackWithOrigin}
      continueWithoutDacButtonVariant={'text'}
      skipDacButton={
        isDacSkipButtonVisible && !shouldRenderSkipBelowBack ? (
          <Button
            id="test-skip-dac"
            name="test-skip-dac"
            onClick={skipDacButtonOnClick}
            color="primary"
            fullWidth
            variant={skipDacButtonVariant}
            endIcon={isPremiumOrOldAge ? <ArrowForwardIcon /> : undefined}
            sx={{
              fontWeight: 500,
              fontSize: '15px',
              height: '50px'
            }}
          >
            {skipButtonText}
          </Button>
        ) : null
      }
      secondaryNavigationContent={
        (shouldRenderSkipBelowBack ||
          (isCat4784Experiment && isDacProcessingIndicatorVisible)) && (
          <>
            {isCat4784Experiment && isDacProcessingIndicatorVisible && (
              <Box mt={1}>
                <DacProcessingIndicator />
              </Box>
            )}
            {shouldRenderSkipBelowBack && (
              <Button
                id="test-skip-dac"
                name="test-skip-dac"
                onClick={skipDacButtonOnClick}
                color="primary"
                fullWidth
                variant={skipDacButtonVariant}
                endIcon={isPremiumOrOldAge ? <ArrowForwardIcon /> : undefined}
                sx={{
                  fontWeight: 500,
                  fontSize: '15px',
                  height: '50px'
                }}
              >
                {skipButtonText}
              </Button>
            )}
          </>
        )
      }
      showDacLegalText={showDacLegalText}
    />
  )
})

export default memo(FormNavigation)
